import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Cloud, CloudOff, RefreshCw, Loader2, ExternalLink, Unlink, UploadCloud } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

export default function DriveStatusCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/drive/status");
      setStatus(data);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    load();
    const flag = params.get("drive");
    if (flag === "connected") {
      toast.success("Google Drive berhasil terhubung");
      params.delete("drive");
      setParams(params, { replace: true });
    } else if (flag === "error") {
      toast.error(`Gagal menghubungkan Drive: ${params.get("msg") || ""}`);
      params.delete("drive");
      params.delete("msg");
      setParams(params, { replace: true });
    }
  }, [load, params, setParams]);

  const connect = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/drive/connect");
      window.location.href = data.authorization_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal memulai OAuth");
      setLoading(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Putuskan koneksi Google Drive? Berkas siswa yang sudah tersimpan tidak akan dihapus.")) return;
    try {
      await api.post("/drive/disconnect");
      toast.success("Google Drive diputus");
      load();
    } catch {
      toast.error("Gagal");
    }
  };

  const migrateAll = async () => {
    if (!window.confirm("Pindahkan SEMUA berkas siswa dari penyimpanan lokal Emergent ke Google Drive lalu hapus salinan lokal? Proses bisa memakan waktu beberapa menit.")) return;
    setMigrating(true);
    try {
      const { data } = await api.post("/drive/migrate-all");
      toast.success(`Berhasil pindahkan ${data.moved} berkas dari ${data.students} siswa ke Drive.`);
      if ((data.errors || []).length) {
        toast.warning(`${data.errors.length} berkas gagal dipindah. Cek log server.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal memindah");
    } finally {
      setMigrating(false);
    }
  };

  if (!status) return null;
  const connected = status.connected;

  return (
    <Card className="mb-6" data-testid="drive-status-card">
      <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className={"w-10 h-10 rounded-lg grid place-items-center flex-shrink-0 " + (connected ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-100 text-slate-500 border border-slate-200")}>
          {connected ? <Cloud className="w-5 h-5" /> : <CloudOff className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-slate-900">Google Drive Sekolah</span>
            {connected ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-emerald-100 text-emerald-800">
                <span className="w-1 h-1 rounded-full bg-emerald-500" /> Terhubung
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-slate-100 text-slate-600">
                Belum Terhubung
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-0.5 truncate">
            {connected
              ? `Struktur: ${status.root_folder}/{Kelas}/{NISN}_{Nama}/ — sinkronisasi otomatis`
              : "Sambungkan ke Google Drive smp1penengahan@gmail.com untuk backup otomatis"}
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0 flex-wrap">
          {connected ? (
            <>
              <Button size="sm" onClick={migrateAll} disabled={migrating} className="bg-emerald-600 hover:bg-emerald-700" data-testid="drive-migrate-all-button">
                {migrating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <UploadCloud className="w-3.5 h-3.5 mr-1.5" />}
                Pindahkan Semua ke Drive
              </Button>
              <Button variant="outline" size="sm" onClick={disconnect} data-testid="drive-disconnect-button">
                <Unlink className="w-3.5 h-3.5 mr-1.5" /> Putuskan
              </Button>
            </>
          ) : (
            <Button size="sm" onClick={connect} disabled={loading} className="bg-blue-700 hover:bg-blue-800" data-testid="drive-connect-button">
              {loading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <ExternalLink className="w-3.5 h-3.5 mr-1.5" />}
              Sambungkan Drive
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
